import { useEffect } from 'react'
import { Banner, Card, Heading, Icon, IconName } from '@boltenergy-be/design-system'
import { useTranslation } from 'react-i18next'
import styles from './BillingCycles.module.scss'
import { Link as RRLink, Navigate } from 'react-router'
import Link from 'components/link/Link.tsx'
import { routes } from 'types/routes'
import { UserTypes } from 'store/auth/types'
import { isActiveContract, isCloseToTermination, isTerminatedContractPastEndDate } from 'utils/contracts'
import { useStoreSelector } from 'hooks/store'
import { useGetPreviousBillingCyclePeriodsQuery } from 'store/queries/bolt-api/contracts'
import { useGetBillShockQuery } from 'store/queries/bolt-backend/billing-contracts'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import dayjs from 'dayjs'
import BillshockError from './components/billshock-error/BillshockError.tsx'
import { DATE_FORMAT } from 'constants/constants.ts'
import { PreviousBillingCyclePeriod } from 'types/billShock.ts'
import NavigationItemContent from 'layouts/components/navigation/navigation-item-content/NavigationItemContent.tsx'
import EstimatedSettlement from 'components/estimated-settlement/EstimatedSettlement.tsx'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'

const BillingCyclesOverview = () => {
  // REDUX STORE
  const { userType } = useStoreSelector((store) => store.auth)
  const { selected, contact } = useStoreSelector((store) => store.contact)
  const { electricity, gas } = useStoreSelector(selectCurrentServiceContracts)
  const serviceContracts = { electricity, gas: isActiveContract(gas) ? gas : undefined }

  // Access rights
  const billingCyclesAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.billingCycles)
  const terminatedContractPastEndDate = isTerminatedContractPastEndDate(serviceContracts.electricity)
  const skipBillingCycles = !contact || !selected.billingContract

  // Redux queries
  const {
    data: billShockData,
    isLoading: billShockLoading,
    error: billShockError
  } = useGetBillShockQuery(
    { billingContractId: selected.billingContract, email: contact?.email || '', serviceContracts: { electricity, gas } },
    { skip: skipBillingCycles || !billingCyclesAccessRights.canAccess.current || terminatedContractPastEndDate }
  )
  const {
    data: periods,
    isLoading: periodsLoading,
    error: periodsError
  } = useGetPreviousBillingCyclePeriodsQuery(selected.billingContract, {
    skip: skipBillingCycles || !billingCyclesAccessRights.showContent.previous
  })

  // i18n
  const { t } = useTranslation(['billing', 'common'])

  // Constants
  const closeToTermination = isCloseToTermination(serviceContracts.electricity)

  /**
   * Tracks
   */
  useEffect(() => {
    if (billingCyclesAccessRights.showContent.current && userType === UserTypes.CUSTOMER) {
      trackEvent({ category: 'billing', object: 'current_billing_cycle', action: 'view' })
    }
  }, [billingCyclesAccessRights, selected.billingContract, userType])

  /**
   * Get the icons for the billing cycle period
   * @param {PreviousBillingCyclePeriod["fuel"]} fuel
   * @param {boolean} combined - Whether to show a combined icon for electricity and gas or separate icons
   */
  const getPeriodIcons = (fuel: PreviousBillingCyclePeriod['fuel'], combined?: boolean): IconName[] => {
    const fuelIcons: IconName[] = []
    if (fuel?.electricity && fuel?.gas && combined) {
      fuelIcons.push('electricityGas')
    } else {
      if (fuel?.electricity) fuelIcons.push('electricity')
      if (fuel?.gas) fuelIcons.push('gas')
    }

    return fuelIcons
  }

  return !billingCyclesAccessRights.canAccess.current && !billingCyclesAccessRights.canAccess.previous ? (
    <Navigate to={routes.BILLING} replace />
  ) : (
    <div className={styles['billing-cycles-overview']}>
      {/* CURRENT BILLING CYCLE */}
      {billingCyclesAccessRights.canAccess.current && !terminatedContractPastEndDate && (
        <Card className={styles['current-card']}>
          {closeToTermination ? (
            <>
              <Heading as="h2" variant="h5">
                {t(($) => $.billingCycles.current.title)}
              </Heading>
              <Banner type="informative">
                {t(($) => $.closeToTermination, {
                  ns: 'common'
                })}
              </Banner>
            </>
          ) : billShockLoading ? (
            <>
              <Heading as="h2" variant="h5">
                {t(($) => $.billingCycles.current.title)}
              </Heading>
              <LoadingSkeleton>
                <LoadingSkeleton.Rectangle height={56} />
              </LoadingSkeleton>
            </>
          ) : billShockData ? (
            <>
              <header>
                <Heading as="h2" variant="h5" weight="semi-bold" className="mb-200">
                  {t(($) => $.billingCycles.current.title)}
                </Heading>
                <div className={styles['date-range']}>
                  <div className={styles.icons}>
                    {getPeriodIcons(
                      {
                        electricity: !!billShockData.billShock.meters.electricity,
                        gas: !!billShockData.billShock.meters.gas
                      },
                      true
                    ).map((icon) => (
                      <Icon key={icon} name={icon} />
                    ))}
                  </div>
                  {dayjs(billShockData.billShock.settlement.startDate).format(DATE_FORMAT)} –{' '}
                  {dayjs(billShockData.billShock.settlement.endDate).format(DATE_FORMAT)}
                </div>
              </header>

              {billingCyclesAccessRights.showContent.current && (
                <>
                  <hr />

                  <dl>
                    <EstimatedSettlement
                      settlementAmount={billShockData.billShock.settlement.billShockAmount}
                      loading={billShockLoading}
                      alwaysOneLine
                    />
                  </dl>

                  <Link representation="button" isFullwidth href={routes.BILLING_INSTALMENT_CHECK}>
                    {t(($) => $.billingCycles.current.viewDetails)}
                  </Link>
                </>
              )}
            </>
          ) : (
            billShockError && <BillshockError error={billShockError} />
          )}
        </Card>
      )}

      {/* PREVIOUS BILLING CYCLE */}
      {billingCyclesAccessRights.canAccess.previous && (
        <section>
          <Heading as="h2" variant="h5">
            {t(($) => $.billingCycles.previous.title)}
          </Heading>

          {periodsLoading ? (
            <LoadingSkeleton>
              <LoadingSkeleton.Rectangle height={56} />
            </LoadingSkeleton>
          ) : periodsError ? (
            <BillshockError error={periodsError} />
          ) : periods?.length ? (
            <ul>
              {periods.map((period) => (
                <li key={period.id}>
                  <RRLink
                    to={routes.BILLING_CYCLE_DETAIL.replace(':id', period.id)}
                    className={styles['cycle-link']}
                    onClick={() =>
                      trackEvent({ category: 'billing', object: 'billing_cycle_detail', action: 'view' }, { cycle: 'previous' })
                    }
                  >
                    <NavigationItemContent
                      isMore
                      icon={getPeriodIcons(period?.fuel)}
                      text={`${dayjs(period.startDate).format(DATE_FORMAT)} – ${dayjs(period.endDate).format(DATE_FORMAT)}`}
                      iconClass={styles.icon}
                    />
                  </RRLink>
                </li>
              ))}
            </ul>
          ) : (
            <Banner>{t(($) => $.billingCycles.previous.noneFound)}</Banner>
          )}
        </section>
      )}
    </div>
  )
}

export default BillingCyclesOverview
